import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from './FormManager';
import { blogPosts } from '../blog-data';
import './BlogPage.css';

const BlogPage = () => {
  const { t } = useTranslation();
  const { openGeneralForm } = useForm();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeFaq, setActiveFaq] = useState(null);
  
  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const animateOnScroll = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    };
    
    const observer = new IntersectionObserver(animateOnScroll, observerOptions);
    
    // Observe blog items
    const blogItems = document.querySelectorAll('.blog-card');
    blogItems.forEach(item => {
      observer.observe(item);
    });
    
    return () => {
      blogItems.forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };



  const categories = [
    { id: 'all', name: 'Alle Artikel' },
    { id: 'seo', name: 'SEO' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'web', name: 'Webdesign' },
    { id: 'social', name: 'Social Media' },
    { id: 'content', name: 'Content' }
  ];

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <>
      <Helmet>
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Enzlo Blog – Digitales Wachstum für lokale Unternehmen",
          "description": "Tipps, Guides und Insights zu Webdesign, SEO, Google Ads und Online-Marketing für lokale Unternehmen, Handwerker und Dienstleister in Deutschland.",
          "url": "https://enzlo.com/blog",
          "publisher": {
            "@type": "Organization",
            "name": "Spoddig UG (haftungsbeschränkt)",
            "legalName": "Spoddig UG (haftungsbeschränkt), HRB 234397 B",
            "logo": {
              "@type": "ImageObject",
              "url": "https://enzlo.com/assets/logo.png"
            },
            "sameAs": [
              "https://www.facebook.com/enzloinc/",
              "https://www.youtube.com/@Totalemailautoresponder"
            ]
          }
        }
      `}</script>
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Wie oft erscheinen neue Blogartikel?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "In der Regel veröffentlichen wir alle 2-3 Wochen neue Beiträge zu SEO, Webdesign und Online-Marketing."
              }
            },
            {
              "@type": "Question",
              "name": "Für wen sind die Inhalte gedacht?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Unsere Inhalte richten sich an lokale Unternehmen, Handwerksbetriebe und Dienstleister in Frankfurt, Darmstadt, Stuttgart, Ulm und Umgebung."
              }
            },
            {
              "@type": "Question",
              "name": "Kann ich eigene Themen vorschlagen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ja, gerne! Schreiben Sie uns einfach eine E-Mail mit Ihrem Themenwunsch."
              }
            }
          ]
        }
      `}</script>
        <title>Blog – Enzlo: Online-Marketing, SEO & Webdesign Tipps für Frankfurt, Darmstadt, Stuttgart & Ulm</title>
        <meta name="description" content="Der Enzlo Blog: Aktuelle Strategien, Tipps und Praxiswissen zu SEO, Webdesign, Google Ads und Online-Marketing für Unternehmen in Frankfurt, Darmstadt, Stuttgart, Ulm und Umgebung." />
        <link rel="canonical" href="https://enzlo.com/blog" />
        {/* OpenGraph & Twitter Card */}
        <meta property="og:title" content="Blog – Enzlo: Online-Marketing, SEO & Webdesign Tipps für Frankfurt, Darmstadt, Stuttgart & Ulm" />
        <meta property="og:description" content="Der Enzlo Blog: Aktuelle Strategien, Tipps und Praxiswissen zu SEO, Webdesign, Google Ads und Online-Marketing für Unternehmen in Frankfurt, Darmstadt, Stuttgart, Ulm und Umgebung." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://enzlo.com/blog" />
        <meta property="og:image" content="https://enzlo.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog – Enzlo: Online-Marketing, SEO & Webdesign Tipps" />
        <meta name="twitter:description" content="Der Enzlo Blog: Strategien, Tipps und Praxiswissen für mehr Sichtbarkeit in Ihrer Region." />
        <meta name="twitter:image" content="https://enzlo.com/og-image.jpg" />
        {/* BreadcrumbList structured data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://enzlo.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://enzlo.com/blog"
              }
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Wie oft erscheinen neue Blogartikel?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In der Regel veröffentlichen wir alle 2-3 Wochen neue Beiträge zu SEO, Webdesign und Online-Marketing."
          }
        },
        {
          "@type": "Question",
          "name": "Für wen sind die Inhalte gedacht?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Unsere Inhalte richten sich an lokale Unternehmen, Handwerksbetriebe und Dienstleister in Frankfurt, Darmstadt, Stuttgart, Ulm und Umgebung."
          }
        },
        {
          "@type": "Question",
          "name": "Kann ich eigene Themen vorschlagen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, gerne! Schreiben Sie uns einfach eine E-Mail mit Ihrem Themenwunsch."
          }
        }
      ]
    }
  `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-background">
          <div className="blog-hero-shape blog-hero-shape-1"></div>
          <div className="blog-hero-shape blog-hero-shape-2"></div>
        </div>
        
        <div className="container">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">
              {t('blogTitle', 'Unser Blog')}
            </h1>
            
            <p className="blog-hero-description">
              {t('blogDescription', 'Tipps, Strategien und Einblicke für Ihr erfolgreiches Online-Marketing')}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Categories */}
      <section className="blog-categories">
        <div className="container">
          <div className="category-filters">
            {categories.map(category => (
              <button 
                key={category.id}
                className={`category-filter ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Blog Posts Grid */}
      <section className="blog-grid">
        <div className="container">
          <div className="blog-posts-grid">
            {filteredPosts.map(post => (
              <article key={post.id} className="blog-card">
                <Link to={`/blog/${post.slug}`} className="blog-card-link">
                  <div className="blog-card-image-container">
                    <div className="blog-card-category">{categories.find(cat => cat.id === post.category).name}</div>
                    <picture>
                      <source srcSet={post.image.replace(/\.(jpg|jpeg|png)$/, '.webp')} type="image/webp" />
                      <img src={post.image} loading="lazy" alt={t('blogCardImageAlt', { title: post.title, category: categories.find(cat => cat.id === post.category).name })} className="blog-card-image" />
                    </picture>
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="blog-card-date">{post.date}</span>
                      <span className="blog-card-dot">•</span>
                      <span className="blog-card-read-time">{post.readTime}</span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-author">
                      <span className="blog-card-author-name">Von {post.author}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="blog-cta">
        <div className="container">
          <div className="blog-cta-content">
            <h2 className="blog-cta-title">{t('blogCtaTitle', 'Bereit, Ihre Online-Präsenz zu verbessern?')}</h2>
            <p className="blog-cta-description">{t('blogCtaDescription', 'Lassen Sie uns gemeinsam Ihre digitale Strategie entwickeln.')}</p>
            <button className="blog-cta-button" onClick={openGeneralForm}>
              <span className="blog-cta-text">{t('blogCtaPrimary', 'Kostenlose Beratung')}</span>
              <span className="blog-cta-arrow">→</span>
            </button>
            <div className="blog-internal-links" style={{marginTop: '1.5rem'}}>
              <h4>Weitere Leistungen:</h4>
              <ul style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', listStyle: 'none', padding: 0}}>
                <li><Link to="/services#webdesign-frankfurt">Webdesign Frankfurt</Link></li>
                <li><Link to="/services#seo-frankfurt">SEO Frankfurt</Link></li>
                <li><Link to="/services#google-ads-frankfurt">Google Ads Frankfurt</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="blog-faq">
        <div className="container">
          <h2>FAQ</h2>
          <div className="faq-list">
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''}`} onClick={() => toggleFaq(0)}>
              <h3>Wie oft erscheinen neue Blogartikel?</h3>
              <div className="faq-content">
                <p>In der Regel veröffentlichen wir alle 2-3 Wochen neue Beiträge zu SEO, Webdesign und Online-Marketing.</p>
              </div>
            </div>
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''}`} onClick={() => toggleFaq(1)}>
              <h3>Für wen sind die Inhalte gedacht?</h3>
              <div className="faq-content">
                <p>Unsere Inhalte richten sich an lokale Unternehmen, Handwerksbetriebe und Dienstleister in Frankfurt, Darmstadt, Stuttgart, Ulm und Umgebung.</p>
              </div>
            </div>
            <div className={`faq-item ${activeFaq === 2 ? 'active' : ''}`} onClick={() => toggleFaq(2)}>
              <h3>Kann ich eigene Themen vorschlagen?</h3>
              <div className="faq-content">
                <p>Ja, gerne! Schreiben Sie uns einfach eine E-Mail mit Ihrem Themenwunsch.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;
